/**
 * Keeps the GTM dataLayer's login fields in step with the MyHU (Azure B2C) session.
 *
 * Asana ID-1694: `LoggedStatus` was always 0 and `userID` always null. Both were
 * read from `localStorage.customer_id`, a key the old Loyalty login wrote and
 * nothing has written since the move to Azure IdM.
 *
 * Two separate fields, because they answer different questions:
 *
 *  - `userID` is the identity of a *known* user: the IdM (Azure B2C) user id, the
 *    `sub` claim of the id token, which is also what the BOL adopts as its user id
 *    and what Salesforce stores on the record. It is **null while nobody is signed
 *    in** — Google's guidance is that GA4 `user_id` identifies a logged-in person,
 *    so feeding it a per-device cookie id inflates the user count and mixes
 *    anonymous traffic into the User-ID reporting identity. Until 2026-09-10 this
 *    field carried the cookie id for anonymous visitors and the container mapped
 *    it to `user_id`; the analytics team asked for the split.
 *  - `session_UUID` is the visitor/device id the BOL keeps in the `hu-bol-uuid`
 *    cookie, which `layout.antlers.html` reads (or mints) before GTM loads and
 *    exposes as `window.huAnonymousId`. It is sent in both states, so a report can
 *    still stitch a session together without a login. Note the BOL rewrites that
 *    cookie to the IdM id after a login, so for a returning signed-in visitor the
 *    two fields can hold the same value — that is the BOL's doing, not ours.
 *
 * MSAL resolves the session asynchronously (redirect handling, then — only when
 * the cache holds no account — ssoSilent, a round trip to B2C), so this runs from
 * `onAuthStateChange` and pushes a `login_status` event once the state is known.
 * GTM fires the GA4 page_view on that custom event and reads `userID` /
 * `LoggedStatus` from it, so the event has to come fast and has to come always:
 * `startLoginStatusTimeout()` pushes the current best knowledge if MSAL has not
 * answered within a bound, and a later, different state is pushed again (GTM
 * then updates the user id). Identical states are pushed once.
 *
 * Only an opaque GUID reaches the data layer, never the email or the name.
 */

const EVENT = 'login_status';

// Long enough for the cache path (tens of ms) and for a healthy ssoSilent, short
// enough that a slow or blocked B2C iframe (MSAL waits up to 10 s) does not hold
// the page_view hostage.
export const LOGIN_STATUS_TIMEOUT_MS = 2500;

let lastPushed = null;

/**
 * Call once, when the page starts waiting for MSAL. If nothing has been pushed
 * by the deadline, report the visitor as anonymous with the cookie id; a real
 * answer that differs (a login) still goes out afterwards.
 */
export function startLoginStatusTimeout(ms = LOGIN_STATUS_TIMEOUT_MS) {
    return setTimeout(() => {
        if (lastPushed === null) {
            pushLoginStatus({ isAuthenticated: false, account: null, inProgress: false });
        }
    }, ms);
}

export function idmUserId(account) {
    if (!account) {
        return null;
    }

    return account.idTokenClaims?.sub || account.localAccountId || null;
}

export function pushLoginStatus({ isAuthenticated, account, inProgress }) {
    if (inProgress) {
        return;
    }

    const loggedIn = Boolean(isAuthenticated && account);
    const payload = {
        event: EVENT,
        LoggedStatus: loggedIn ? 1 : 0,
        // No `|| window.huAnonymousId` fallback here on purpose: it used to hide a
        // missing `sub` behind the cookie id, which reported an anonymous id
        // together with `LoggedStatus: 1`. A signed-in account with no usable id is
        // worth seeing as null.
        userID: loggedIn ? idmUserId(account) : null,
        session_UUID: window.huAnonymousId || null,
    };

    // auth.js notifies again on every window focus for anonymous visitors (it
    // retries ssoSilent), so only push when the reported identity changed.
    const key = `${payload.LoggedStatus}:${payload.userID}:${payload.session_UUID}`;
    if (key === lastPushed) {
        return;
    }
    lastPushed = key;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
}
